import React, { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
    Card,
    Text,
    TextInput,
    Button,
    H4,
    Dialog,
} from '@revdotcom/vaporwave';

import { useSubmit } from '../useSubmit';
import { fixupEvents } from '../helpers';
import { type MfaPageProps } from '../../codegen/MfaPageProps';
import { type MfaPageSubmit } from '../../codegen/MfaPageSubmit';
import styles from '../../styles';
import { CardLogo } from '../components/CardLogo';
import { UnderLinks } from '../components/UnderLinks';
import { type FlowProps } from '../../FlowProps';

const AppCodeRegex = /^[0-9]{6}$/;
const EmailCodeRegex = /^[0-9]{8}$/;

/**
 * MFA code submission in the login flow
 * @param {MfaPageProps} props page parameters
 * @returns {Element} JSX
 */
const MfaPage = (props: FlowProps<MfaPageProps>) => {
    const [searchParams] = useSearchParams();

    // enable react-hook-form form orchestration
    const {
        register,
        handleSubmit,
        formState: { isSubmitting, isValid },
        setValue,
        setFocus,
    } = useForm<MfaPageSubmit>({ mode: 'onChange' });

    const returnUrl = searchParams.get('returnUrl') ?? 'missing';
    const state = searchParams.get('state') ?? 'missing';

    // set up our ajax submit handler
    const { onSubmit, submitError, isRedirecting, resetSubmitError } =
        useSubmit('mfa', {
            antiforgeryToken: props.verificationToken,
            preSubmit: useCallback(
                (data: MfaPageSubmit) => {
                    // post back
                    data.returnUrl = returnUrl;
                    data.state = state;
                },
                [returnUrl, state],
            ),
        });

    const [emailDialogDisplayed, setEmailDialogDisplayed] = useState(false);
    const [emailSent, setEmailSent] = useState(props.isCodeFromEmail);
    const [isSendingEmail, setSendingEmail] = useState(false);

    const pattern = emailSent ? EmailCodeRegex : AppCodeRegex;
    const text = emailSent
        ? 'Please enter the verification code that was sent to your email'
        : 'Please enter the verification code from your authentication app';

    const submitLabel = props.isLoginConfirmation ? 'Confirm' : 'Sign in';

    // auto-focus on code entry
    useEffect(() => {
        setFocus('code');
    });

    /**
     *
     */
    async function sendEmail() {
        // reset form state
        resetSubmitError();
        setValue('code', '');
        setEmailDialogDisplayed(false);

        // ONEDAY: error handling
        setSendingEmail(true);
        await fetch(props.emailLink || 'must be set');
        setEmailSent(true);
        setSendingEmail(false);
    }

    return (
        <Card sx={styles.cardLogin}>
            <CardLogo {...props} />

            <div sx={styles.cardContainer}>
                <H4 sx={styles.cardTitle}>2FA Code</H4>

                <form sx={styles.cardForm} onSubmit={handleSubmit(onSubmit)}>
                    <div sx={{ position: 'relative' }}>
                        <TextInput
                            id="mfa-code-input"
                            label={text}
                            inputMode="numeric"
                            size="large"
                            /*isRequired */
                            sx={{ variant: 'forms.identity.login.mfaCode' }}
                            {...fixupEvents(
                                register('code', {
                                    required: true,
                                    pattern,
                                }),
                            )}
                        />
                        {submitError && (
                            <Text sx={styles.cardError}>{submitError}</Text>
                        )}
                    </div>

                    <Button
                        id="submit-button"
                        sx={{
                            display: 'block',
                            variant: 'forms.identity.login.submit',
                        }}
                        type="submit"
                        isDisabled={isSubmitting || !isValid || isRedirecting}
                    >
                        {submitLabel}
                    </Button>
                </form>

                {!emailSent && props.emailLink && (
                    <>
                        <Button
                            id="no-app-button"
                            sizeVariant="small"
                            variant="text"
                            onPress={() => setEmailDialogDisplayed(true)}
                            isDisabled={isSendingEmail}
                            sx={styles.cardSendMfaCode}
                        >
                            Don&apos;t have access to your app?
                        </Button>

                        <Dialog
                            isOpen={emailDialogDisplayed}
                            isDismissable
                            onClose={() => setEmailDialogDisplayed(false)}
                            title="Don't have access to your app?"
                            sx={styles.mfaDialog}
                        >
                            <Text sx={styles.mfaDialogText}>
                                If you don&apos;t have access to your
                                authenticator app, please click the button below
                                to receive an 8-digit code sent to your e-mail
                                address.
                            </Text>
                            <Button
                                id="send-email-button"
                                onPress={sendEmail}
                                sizeVariant="small"
                                sx={styles.mfaDialogButton}
                            >
                                Email Me a 2FA Code
                            </Button>
                        </Dialog>
                    </>
                )}
            </div>
            <UnderLinks {...props} />
        </Card>
    );
};

export default MfaPage;
